import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { Routes } from '../../config/routes';
import { BillingEventsService } from '../billing/billing-events.service';
import { SessionService } from '../storage/session.service';

/** One open, PMC-billed invoice. Amounts are cents. */
export interface PmcOutstandingInvoice
{
    id: string;
    number: string | null;
    organizationId: number | null;
    organizationName: string | null;
    productName: string | null;
    amountRemaining: number;
    invoicePdf: string | null;
    hostedInvoiceUrl: string | null;
    periodEnd: number | null;
}

export interface PmcOutstandingInvoices
{
    pmcId: number;
    hasPaymentMethod: boolean;
    totalDue: number;
    invoices: PmcOutstandingInvoice[];
}

/**
 * The PMC dashboard's payment method pop-up (SD-10448): what a PMC owes on the subscriptions it pays for its
 * communities, and paying all of it at once.
 */
@Injectable({
    providedIn: 'root',
})
export class PmcOutstandingInvoicesService
{
    /** sessionStorage key: the PMCs whose pop-up was dismissed, so it stays closed until the next login. */
    static readonly DISMISSED_KEY = 'pmcPaymentMethodPopupDismissed';

    constructor(
            private _http: ApplicationHttpClient,
            private _billingEvents: BillingEventsService,
    ) {
    }

    /** The pop-up shows only when the PMC owes something and has no payment method to collect it with. */
    static requiresPaymentMethod(outstanding: PmcOutstandingInvoices | null): boolean {
        return !!outstanding && !outstanding.hasPaymentMethod && outstanding.invoices.length > 0;
    }

    static isDismissed(pmcId: number): boolean {
        return PmcOutstandingInvoicesService.dismissedPmcIds().includes(pmcId);
    }

    static dismiss(pmcId: number): void {
        const ids = PmcOutstandingInvoicesService.dismissedPmcIds();
        if (!ids.includes(pmcId)) {
            SessionService.set(PmcOutstandingInvoicesService.DISMISSED_KEY, [...ids, pmcId].join(','));
        }
    }

    /** Called on logout, so a dismissal lasts until the next login and no longer. */
    static clearDismissals(): void {
        SessionService.destroy(PmcOutstandingInvoicesService.DISMISSED_KEY);
    }

    private static dismissedPmcIds(): number[] {
        return (SessionService.get(PmcOutstandingInvoicesService.DISMISSED_KEY) || '')
                .split(',')
                .filter(Boolean)
                .map(Number);
    }

    getOutstandingInvoices(pmcId: number): Observable<PmcOutstandingInvoices> {
        const url = Routes.MakeLegFiCoreUrl(Routes.StripeModule.PmcOutstandingInvoices(pmcId));

        return this._http.get<{ data: PmcOutstandingInvoices }>(url).pipe(
                map((response) => response.data),
        );
    }

    /**
     * Pays every outstanding invoice with the given method. The method becomes the PMC's default only if it paid
     * something, so a declined card leaves `hasPaymentMethod` false and the invoices in the response.
     */
    payOutstandingInvoices(pmcId: number, stripePaymentMethodId: string): Observable<PmcOutstandingInvoices> {
        const url = Routes.MakeLegFiCoreUrl(Routes.StripeModule.PayPmcOutstandingInvoices(pmcId));

        return this._http.post<{ data: PmcOutstandingInvoices }>(url, JSON.stringify({stripePaymentMethodId})).pipe(
                map((response) => response.data),
                tap(() => {
                    this._billingEvents.invoiceChanged$.next({context: 'pmcs', id: pmcId});
                    this._billingEvents.paymentMethodChanged$.next({context: 'pmcs', id: pmcId});
                }),
        );
    }
}
